import type { IncidentView } from "./incident-model.js";

type T = (k: string, p?: Record<string, string | number>) => string;

/** Freshness thresholds (minutes) and the floor opacity for a fully-stale incident. Tunable. */
export const FRESH_MIN = 10;
export const STALE_MIN = 60;
export const OPACITY_FLOOR = 0.5;

const KNOWN_FEEDS = new Set(["nasa-firms", "fogos.pt", "anepc"]);

export type StaleBucket = "fresh" | "aging" | "stale";

function isActive(v: IncidentView): boolean {
  return v.status === "open" || v.status === "sustained";
}

export function isWorsening(v: IncidentView): boolean {
  return v.trend?.worsening === true;
}

/** Minutes since the incident was last confirmed (falls back to first report). */
export function minutesSinceConfirmed(v: IncidentView, nowMs: number): number {
  const then = Date.parse(v.lastConfirmedAt ?? v.createdAtIso ?? "");
  if (Number.isNaN(then)) return 0;
  return Math.max(0, (nowMs - then) / 60000);
}

export function staleBucket(v: IncidentView, nowMs: number): StaleBucket {
  const m = minutesSinceConfirmed(v, nowMs);
  if (m < FRESH_MIN) return "fresh";
  if (m < STALE_MIN) return "aging";
  return "stale";
}

/** Faded = active, not worsening, and past the fresh window. */
export function isFaded(v: IncidentView, nowMs: number): boolean {
  return isActive(v) && !isWorsening(v) && staleBucket(v, nowMs) !== "fresh";
}

/** Opacity for map markers / rows: 1.0 when fresh/worsening/inactive, ramping to OPACITY_FLOOR. */
export function agingOpacity(v: IncidentView, nowMs: number): number {
  if (!isActive(v) || isWorsening(v)) return 1;
  const m = minutesSinceConfirmed(v, nowMs);
  if (m <= FRESH_MIN) return 1;
  if (m >= STALE_MIN) return OPACITY_FLOOR;
  const frac = (m - FRESH_MIN) / (STALE_MIN - FRESH_MIN);
  return 1 - frac * (1 - OPACITY_FLOOR);
}

/** Plain qualitative trend: worsening always reads "rising"; else the intensity axis. */
export function trendLabelKey(v: IncidentView): "rising" | "steady" | "falling" {
  if (v.trend?.worsening) return "rising";
  const i = v.trend?.intensity;
  return i === "rising" || i === "falling" ? i : "steady";
}

/** Stable comparator: worsening incidents first, all others keep their prior order. */
export function worseningFirst<T extends { view: IncidentView }>(a: T, b: T): number {
  return Number(isWorsening(b.view)) - Number(isWorsening(a.view));
}

/** Friendly source names for feed-bearing contributors (deduped); identity-only entries are technical. */
export function contributorLabels(t: T, v: IncidentView): string[] {
  const out: string[] = [];
  for (const c of v.contributors ?? []) {
    if (!c.feed) continue;
    out.push(KNOWN_FEEDS.has(c.feed) ? t(`incident.source.${c.feed}`) : c.feed);
  }
  return [...new Set(out)];
}

function axis(t: T, dir: string): string {
  return t(`incident.detail.adv.axis.${dir === "rising" || dir === "falling" ? dir : "steady"}`);
}

/** Technical rows for the Advanced collapsible. Length > 2 gates whether the collapsible renders. */
export function technicalRows(t: T, v: IncidentView): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  if (typeof v.currentFrpMw === "number") {
    const peak = typeof v.peakFrpMw === "number" ? ` ${t("incident.detail.adv.peak", { n: Math.round(v.peakFrpMw) })}` : "";
    rows.push({ label: t("incident.detail.adv.intensity"), value: `${t("incident.detail.adv.mw", { n: Math.round(v.currentFrpMw) })}${peak}` });
  }
  if (v.trend) {
    rows.push({ label: t("incident.detail.adv.trendAxes"), value: `${axis(t, v.trend.intensity)} · ${axis(t, v.trend.footprint)} · ${axis(t, v.trend.corroboration)}` });
  }
  if (typeof v.W === "number") rows.push({ label: t("incident.detail.adv.weight"), value: v.W.toFixed(1) });
  const ids = (v.contributors ?? []).filter((c) => c.identity && !c.feed).map((c) => c.identity as string);
  if (ids.length) rows.push({ label: t("incident.detail.adv.identities"), value: ids.join(", ") });
  return rows;
}
